/**
 * CR313: Feature Flag Row Component
 * 
 * Displays a single flag with inline toggle for booleans, summary for rollouts.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Settings2 } from 'lucide-react';
import type { FeatureFlagState } from '@/hooks/admin/useFeatureFlagsAdmin';
import type { RolloutValue } from '@/lib/admin/featureFlagsRegistry';

interface FeatureFlagRowProps {
  flag: FeatureFlagState;
  draftValue: unknown;
  onValueChange: (value: unknown) => void;
  onOpenDrawer: () => void;
}

function getRolloutSummary(value: unknown): string {
  if (!value || typeof value !== 'object') return 'Invalid';
  const v = value as RolloutValue;
  
  switch (v.mode) {
    case 'OFF':
      return 'OFF';
    case 'ON':
      return 'ON (100%)';
    case 'PERCENT':
      return `${v.percent}%`;
    case 'ALLOWLIST':
      return `Allowlist (${v.allowlist?.length || 0})`;
    default:
      return 'Unknown';
  }
}

function getTypeBadgeColor(type: string): string {
  switch (type) {
    case 'boolean':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'rollout':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    case 'json':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    default:
      return '';
  }
}

export function FeatureFlagRow({
  flag,
  draftValue,
  onValueChange,
  onOpenDrawer,
}: FeatureFlagRowProps) {
  const { registryEntry, isOverridden } = flag;
  
  if (!registryEntry) return null;
  
  const handleBooleanToggle = (checked: boolean) => {
    onValueChange(checked);
  };
  
  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b hover:bg-muted/30 transition-colors">
      {/* Label + Key */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{registryEntry.label}</span>
        </div>
        <div className="text-xs text-muted-foreground font-mono truncate mt-0.5">
          {registryEntry.key}
        </div>
      </div>
      
      {/* Type badge */}
      <Badge 
        variant="outline" 
        className={cn('shrink-0 text-xs', getTypeBadgeColor(registryEntry.type))}
      >
        {registryEntry.type}
      </Badge>
      
      {/* Status pill */}
      <Badge 
        variant={isOverridden ? 'default' : 'secondary'}
        className={cn(
          'shrink-0 text-xs',
          isOverridden && 'bg-amber-500 hover:bg-amber-500'
        )}
      >
        {isOverridden ? 'Overridden' : 'Default'}
      </Badge>
      
      {/* Value editor / display */}
      <div className="w-32 flex items-center justify-end shrink-0">
        {registryEntry.type === 'boolean' && (
          <Switch
            checked={draftValue === true}
            onCheckedChange={handleBooleanToggle}
          />
        )}
        
        {registryEntry.type === 'rollout' && (
          <Badge variant="outline" className="font-mono text-xs">
            {getRolloutSummary(draftValue)}
          </Badge>
        )}
        
        {registryEntry.type === 'json' && (
          <span className="text-xs text-muted-foreground italic">
            JSON object
          </span>
        )}
      </div>
      
      {/* Edit button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={onOpenDrawer}
      >
        <Settings2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
